// Language resolution rules for a new (not-yet-authenticated) visitor, in
// priority order:
//   1. If the user understands the preferred language (it appears anywhere in
//      their browser preferences) and it is supported, serve it.
//   2. Otherwise honor the first supported language in their preference list.
//   3. Otherwise fall back to the preferred language.
//
// The preferred language mirrors the backend default and is supplied by the
// caller (see locales.ts), which is where the environment is known:
//   - Production  → French  (Datizy is a French-first product)
//   - Other envs  → English
// PREFERRED_LANGUAGE below is the production default.

export const PREFERRED_LANGUAGE = 'fr';

// Read the browser's ordered language preferences in a non-browser-safe way.
export function getBrowserLanguages(): string[] {
  if (typeof navigator === 'undefined') return [];

  const nav: any = navigator as any;
  if (Array.isArray(nav.languages) && nav.languages.length) {
    return nav.languages as string[];
  }
  if (nav.language) return [String(nav.language)];
  return [];
}

// Pure resolver — keep free of browser globals so it stays unit-testable.
export function resolveLanguage(
  langs: string[],
  supportedLanguages: string[],
  preferredLanguage: string = PREFERRED_LANGUAGE,
): string {
  // Normalize: try full tag (e.g., "en-US") then base ("en")
  const candidates = langs
    .filter(Boolean)
    .map((l) => String(l).toLowerCase())
    .flatMap((l) => [l, l.split('-')[0]]);

  // Preferred-first: if the user understands the preferred language, serve it.
  if (
    supportedLanguages.includes(preferredLanguage) &&
    candidates.includes(preferredLanguage)
  ) {
    return preferredLanguage;
  }

  const match = candidates.find((l) => supportedLanguages.includes(l));
  return match || preferredLanguage;
}
